import styles from "./ProgramDetails.module.scss";
import clsx from "clsx";
import { FormattedMessage } from "react-intl";
import useMaterialPrice from "./useMaterialPrice";
import OffScreen from "common/core-ui/OffScreen";
import { Action } from "../useMaterialActions";
import { ReactNode, useMemo } from "react";
import { MaterialAction } from "../MaterialActions";
import { CourseMaterial, useCoursesLoaderData } from "../coursesLoader";
import useFindCourse from "../useFindCourse";
import { useLayoutData } from "../../layout/myCoursesLayoutLoader";
import PrintDetails from "./PrintDetails";
import { useAllowOptLanguage } from "../useAllowOptLanguage";

export interface ProgramDetailsProps {
  actions: Action[];
  courseMaterial: CourseMaterial;
}

interface StatusDetails {
  title: ReactNode;
  explanation: ReactNode;
  showActions: Action[];
}

const ALWAYS_SHOWN_ACTIONS = ["view_shipping", "update_shipping"];

export default function ProgramDetails(props: ProgramDetailsProps) {
  const { actions, courseMaterial } = props;
  const { courseId, materialStatus, materialFormat } = courseMaterial;
  const course = useFindCourse({ courseId })!;
  const materialPrice = useMaterialPrice(courseMaterial);
  const { currentTerm } = useCoursesLoaderData();
  const printInfo = currentTerm?.printInfo;
  const { config } = useLayoutData();
  const programDecision = currentTerm?.programDecision;
  const allowOptLanguage = useAllowOptLanguage();
  const statusDetails = useMemo((): StatusDetails | null => {
    const showActions = actions.filter((action) =>
      ALWAYS_SHOWN_ACTIONS.includes(action.type ?? "")
    );
    if (materialStatus === "opted_in" && allowOptLanguage) {
      const action = actions.find(
        (action: Action) => action.type === "opt_out"
      );
      return {
        title: (
          <div className={styles.green}>
            <FormattedMessage defaultMessage={"Opted In"} />
          </div>
        ),
        explanation: (
          <FormattedMessage
            defaultMessage={
              "You are opted in. This item is included in your program and will be billed to your student account."
            }
          />
        ),
        showActions: [...showActions, ...(action ? [action] : [])],
      };
    } else if (materialStatus === "continuation" && allowOptLanguage) {
      return {
        title: (
          <div className={styles.green}>
            <FormattedMessage defaultMessage={"Opted In"} />
          </div>
        ),
        explanation: (
          <FormattedMessage
            defaultMessage={
              "You still have access to this item from a previous class."
            }
          />
        ),
        showActions,
      };
    } else if (materialStatus === "opted_out" && allowOptLanguage) {
      const action = actions.find((action: Action) => action.type === "opt_in");
      return {
        title: (
          <div className={styles.red}>
            <FormattedMessage defaultMessage={"Opted Out"} />
          </div>
        ),
        explanation: (
          <FormattedMessage
            defaultMessage={
              "You are opted out of this item. You need to purchase it on your own or opt back in."
            }
          />
        ),
        showActions: [...showActions, ...(action ? [action] : [])],
      };
    } else if (materialStatus === "included") {
      return {
        title: (
          <div className={styles.green}>
            <FormattedMessage defaultMessage={"Included - No Cost"} />
          </div>
        ),
        explanation: (
          <FormattedMessage
            defaultMessage={"You have free access to this item."}
          />
        ),
        showActions,
      };
    } else if (materialStatus === "not_included") {
      const action = actions.find((action: Action) => action.type === "ePOS");
      return {
        title: (
          <div className={styles.red}>
            <FormattedMessage
              defaultMessage={"Not Included - Need to Purchase"}
            />
          </div>
        ),
        explanation: (
          <FormattedMessage
            defaultMessage={
              "This item is not included in your program and you need to purchase on your own."
            }
          />
        ),
        showActions: [...showActions, ...(action ? [action] : [])],
      };
    } else {
      return null;
    }
  }, [materialStatus, actions, allowOptLanguage]);

  return (
    <div className={styles.container}>
      <h3 className={styles.containerTitle}>
        <FormattedMessage defaultMessage="Program Details" />
      </h3>
      <dl
        data-testid="material-program-details"
        className={clsx(
          "vst-definition-list",
          "vst-definition-list-size-small"
        )}
      >
        {(programDecision === "equitable_access" ||
          programDecision === "inclusive_access") && (
          <>
            <dt data-testid="program-name">
              <FormattedMessage defaultMessage="Program" />
            </dt>
            <dd>
              {programDecision === "equitable_access"
                ? config.equitable_access.programName
                : config.inclusive_access.programName}
            </dd>
          </>
        )}

        <dt>
          <FormattedMessage defaultMessage="Course" />
        </dt>
        <dd>{course.title}</dd>

        {materialPrice && (
          <>
            <dt>
              <FormattedMessage defaultMessage="Price" />
            </dt>
            <dd>{materialPrice}</dd>
          </>
        )}

        {statusDetails && (
          <>
            <dt>
              <FormattedMessage defaultMessage="Status" />
            </dt>
            <dd>{statusDetails.title}</dd>
            <dt>
              <OffScreen>
                <FormattedMessage defaultMessage="Status Message" />
              </OffScreen>
            </dt>
            <dd>
              {materialFormat !== "print" && (
                <div
                  data-testid="status-explanation"
                  className={styles.statusMessage}
                >
                  {statusDetails.explanation}
                </div>
              )}
            </dd>
          </>
        )}

        {printInfo && (
          <PrintDetails courseMaterial={courseMaterial} printInfo={printInfo} />
        )}

        {statusDetails && statusDetails.showActions.length > 0 && (
          <>
            <dt>
              <OffScreen>
                <FormattedMessage defaultMessage="Material Actions" />
              </OffScreen>
            </dt>
            <dd>
              <div className={styles.actions}>
                {statusDetails.showActions.map((action) => (
                  <MaterialAction key={action.key} action={action} />
                ))}
              </div>
            </dd>
          </>
        )}
      </dl>
    </div>
  );
}
